import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { trackEvent } from '../eventService';
import { getClientLocation, geolocate } from '../geolocationUtils';
import { useMapCommands } from '../contexts/MapCommandsContext';
import OnboardingPrompt from './OnboardingPrompt';
import { decodeQueryString } from '../queryStringUtils';
import { SyncReactNode } from '../types';

type Props = {
  children: SyncReactNode;
};

const ONBOARDING_EXPIRES_AT = 'onboardingExpiresAt';

const IS_RECENTLY_ONBOARDED = (() => {
  let onboardingExpiresAt;
  try {
    onboardingExpiresAt = window.localStorage.getItem(ONBOARDING_EXPIRES_AT);
  } catch {
    onboardingExpiresAt = null;
  }
  const isPreviouslyOnboarded = onboardingExpiresAt !== null;
  if (isPreviouslyOnboarded) {
    const isExpired = Number(onboardingExpiresAt) < Date.now();
    if (isExpired) {
      try {
        window.localStorage.removeItem(ONBOARDING_EXPIRES_AT);
      } catch {
        // do nothing
      }
      return false;
    }
    return true;
  }
  return false;
})();

const setOnboardingExpiresAt = () => {
  const durationInDays = 30;
  // eslint-disable-next-line no-magic-numbers
  const expiresAt = Date.now() + durationInDays * 24 * 3600 * 1000;
  try {
    window.localStorage.setItem(ONBOARDING_EXPIRES_AT, expiresAt.toString());
  } catch {
    // do nothing
  }
};

const useOnBoarding = () => {
  const mapCommands = useMapCommands();
  const { pathname, search } = useLocation();
  const clientLocation = getClientLocation();
  const queryObject = decodeQueryString(search);
  const isSearchParams = queryObject.searchType && queryObject.searchString;

  const [isOnboarded, setIsOnboarded] = useState(
    IS_RECENTLY_ONBOARDED || clientLocation !== null || pathname !== '/' || isSearchParams,
  );
  const onboardWithLocation = () => {
    geolocate()
      .then(([longitude, latitude]) => {
        // eslint-disable-next-line no-magic-numbers
        mapCommands.panTo([longitude, latitude], 14);
      })
      .catch(() => {
        // eslint-disable-next-line no-alert
        alert(
          'We were unable to get your location. Make sure that your browser supports geolocation and that this website is allowed to get your location.',
        );
      })
      .finally(() => {
        setIsOnboarded(true);
        trackEvent('map', 'interaction', 'geo-target');
      });
  };
  const onboard = () => {
    setIsOnboarded(true);
    setOnboardingExpiresAt();
    trackEvent('map', 'interaction', 'noshare');
  };
  return [isOnboarded, onboardWithLocation, onboard];
};

const OnboardingBoundry = ({ children }: Props) => {
  const [isOnboarded, onboardWithLocation, onboard] = useOnBoarding();

  if (isOnboarded) {
    return children;
  }

  return (
    <OnboardingPrompt
      // @ts-expect-error
      onWithLocationClick={onboardWithLocation}
      // @ts-expect-error
      onWithoutLocationClick={onboard}
    />
  );
};

export default OnboardingBoundry;
